import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ExternalLink, Facebook, Instagram } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { activePublicMenuNovedades } from '@/lib/menu/menuNovedades';
import { formatScheduleForCustomer } from '@/lib/weeklySchedule';
import {
  getCatalogSectionTitleClass,
  getMenuBannerOverlayStyle,
  getMenuMutedTextStyle,
} from '@/lib/menuTheme';
import { CATALOG_HERO_FALLBACK_DESC } from './catalogHelpers';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { TikTokIcon, WhatsAppIcon } from '../welcome/welcomeSocialIcons';

const AUTOPLAY_MS = 5000;
const BANNER_MIN_H = 'min-h-[220px] md:min-h-[300px]';

function hasPublicSocial(business) {
  return Boolean(
    business?.instagram_url ||
      business?.tiktok_url ||
      business?.facebook_url ||
      business?.whatsapp_url,
  );
}

function CatalogSocialLinks({ business, standard, premium, branding }) {
  if (!hasPublicSocial(business)) return null;

  const linkClass = cn(
    'transition-colors p-1 rounded-full',
    standard && 'text-muted-foreground hover:text-foreground',
  );
  const linkStyle = premium ? { color: branding.muted_text_color } : undefined;

  return (
    <nav
      className="flex flex-wrap justify-start items-center gap-2"
      aria-label="Redes sociales del local"
    >
      {business.instagram_url ? (
        <a
          href={business.instagram_url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          style={linkStyle}
          aria-label="Instagram"
        >
          <Instagram className="w-4 h-4" />
        </a>
      ) : null}
      {business.tiktok_url ? (
        <a
          href={business.tiktok_url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          style={linkStyle}
          aria-label="TikTok"
        >
          <TikTokIcon className="w-4 h-4" />
        </a>
      ) : null}
      {business.facebook_url ? (
        <a
          href={business.facebook_url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          style={linkStyle}
          aria-label="Facebook"
        >
          <Facebook className="w-4 h-4" />
        </a>
      ) : null}
      {business.whatsapp_url ? (
        <a
          href={business.whatsapp_url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          style={linkStyle}
          aria-label="WhatsApp"
        >
          <WhatsAppIcon className="w-4 h-4" />
        </a>
      ) : null}
    </nav>
  );
}

function NovedadBannerSlide({ item, onOpenLink }) {
  const content = (
    <div className={cn('relative w-full overflow-hidden', BANNER_MIN_H)}>
      <img
        src={item.image_url}
        alt={item.title || 'Novedad del local'}
        className="absolute inset-0 h-full w-full object-cover"
        loading="eager"
      />
      {item.title ? (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-4 py-4 md:px-8 md:py-6">
          <p className="text-sm md:text-base font-semibold text-white line-clamp-2">{item.title}</p>
        </div>
      ) : null}
      {item.link_url ? (
        <div className="absolute top-3 right-3 rounded-full bg-black/45 p-2 text-white" aria-hidden>
          <ExternalLink className="h-4 w-4" />
        </div>
      ) : null}
    </div>
  );

  if (item.link_url) {
    return (
      <button
        type="button"
        className="block w-full text-left"
        onClick={() => onOpenLink(item.link_url)}
        data-testid={`menu-novedad-banner-${item.id}`}
      >
        {content}
      </button>
    );
  }

  return <div data-testid={`menu-novedad-banner-${item.id}`}>{content}</div>;
}

function CatalogNovedadesBanner({ items, business, premium }) {
  const slides = activePublicMenuNovedades(items);
  const [carouselApi, setCarouselApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const hasMultiple = slides.length > 1;

  const openLink = (url) => {
    const target = String(url || '').trim();
    if (!target) return;
    const href = /^https?:\/\//i.test(target) ? target : `https://${target}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const onSelect = useCallback(() => {
    if (!carouselApi) return;
    setSelectedIndex(carouselApi.selectedScrollSnap());
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return undefined;
    onSelect();
    carouselApi.on('select', onSelect);
    carouselApi.on('reInit', onSelect);
    return () => {
      carouselApi.off('select', onSelect);
      carouselApi.off('reInit', onSelect);
    };
  }, [carouselApi, onSelect]);

  useEffect(() => {
    if (!carouselApi || !hasMultiple || paused) return undefined;
    const timer = window.setInterval(() => carouselApi.scrollNext(), AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [carouselApi, hasMultiple, paused, slides.length]);

  if (!slides.length) return null;

  return (
    <div
      className="relative w-full overflow-hidden"
      data-testid="catalog-menu-novedades-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <Carousel setApi={setCarouselApi} opts={{ align: 'start', loop: hasMultiple }} className="w-full">
        <CarouselContent className="ml-0">
          {slides.map((item) => (
            <CarouselItem key={item.id} className="pl-0 basis-full">
              <NovedadBannerSlide item={item} onOpenLink={openLink} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {hasMultiple ? (
          <>
            <CarouselPrevious className="left-3 md:left-6 border-white/30 bg-black/35 text-white hover:bg-black/50" />
            <CarouselNext className="right-3 md:right-6 border-white/30 bg-black/35 text-white hover:bg-black/50" />
          </>
        ) : null}
      </Carousel>
      {hasMultiple ? (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-10" aria-hidden>
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                'h-1.5 rounded-full transition-all',
                index === selectedIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/45',
              )}
              onClick={() => carouselApi?.scrollTo(index)}
              aria-label={`Ver novedad ${index + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CatalogConfigBanner({ business, premium }) {
  if (!business?.banner_url) return null;

  return (
    <div className={cn('relative w-full overflow-hidden', BANNER_MIN_H)} data-testid="catalog-config-banner">
      <img
        src={business.banner_url}
        alt=""
        className={cn('absolute inset-0 h-full w-full object-cover', premium && 'scale-105')}
      />
      {premium ? (
        <div
          className="pointer-events-none absolute inset-0"
          style={getMenuBannerOverlayStyle(business)}
          aria-hidden
        />
      ) : (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" aria-hidden />
      )}
    </div>
  );
}

function CatalogMenuBusinessInfo({ business, standard, premium, branding, tc }) {
  const { t } = useTranslation();
  const titleClass = getCatalogSectionTitleClass(business, standard);
  const desc = String(business?.description ?? '').trim() || CATALOG_HERO_FALLBACK_DESC;
  const scheduleText = formatScheduleForCustomer(business);
  const schedulePreview = scheduleText
    ? scheduleText.includes('\n')
      ? scheduleText.split('\n').slice(0, 2).join(' · ')
      : scheduleText
    : null;

  return (
    <section
      className={cn(
        'max-w-7xl mx-auto px-4 py-3 md:py-4 text-left border-b',
        standard && 'border-border bg-background',
      )}
      style={
        premium
          ? {
              backgroundColor: branding.background_color,
              borderColor: branding.card_border_color,
            }
          : undefined
      }
      data-testid="catalog-menu-business-info"
    >
      <div className="flex items-center gap-2.5 md:gap-4">
        {business?.logo_url ? (
          <img
            src={business.logo_url}
            alt=""
            className={cn(
              'h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover border-2 shadow-sm shrink-0',
              standard && 'border-border bg-background',
            )}
            style={premium ? { borderColor: branding.card_border_color } : undefined}
          />
        ) : null}

        <div className="flex-1 min-w-0 space-y-1">
          <div>
            <h1
              className={cn('text-base sm:text-lg md:text-xl font-bold tracking-tight leading-tight', titleClass)}
              style={premium ? { color: branding.text_color } : undefined}
            >
              {business?.name
                ? t('catalog.welcomeNamed', { name: business.name })
                : t('catalog.welcome')}
            </h1>
            <p
              className={cn('text-xs leading-snug mt-0.5 line-clamp-2', standard && tc.productMuted)}
              style={premium ? getMenuMutedTextStyle(business) : undefined}
            >
              {desc}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {schedulePreview ? (
              <div
                className={cn(
                  'flex items-center gap-1 text-xs',
                  standard && 'text-muted-foreground',
                )}
                style={premium ? getMenuMutedTextStyle(business) : undefined}
              >
                <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden />
                <span className="line-clamp-1">{schedulePreview}</span>
              </div>
            ) : null}

            <CatalogSocialLinks
              business={business}
              standard={standard}
              premium={premium}
              branding={branding}
            />

            <Badge
              className={cn(
                'text-[11px] px-2 py-0 h-5',
                business?.is_open
                  ? 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent'
                  : standard
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-zinc-600 hover:bg-zinc-600 text-white border-transparent',
              )}
            >
              {business?.is_open ? t('catalog.openNow') : t('catalog.closed')}
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CatalogMenuHero({
  business,
  standard,
  premium,
  branding,
  accent,
  tc,
}) {
  const novedades = activePublicMenuNovedades(business?.menu_novedades);
  const showNovedadesBanner = novedades.length > 0;
  const showConfigBanner = !showNovedadesBanner && Boolean(business?.banner_url);

  return (
    <header className="w-full" data-testid="catalog-menu-hero">
      {showNovedadesBanner ? (
        <CatalogNovedadesBanner items={business.menu_novedades} business={business} premium={premium} />
      ) : null}
      {showConfigBanner ? (
        <CatalogConfigBanner business={business} premium={premium} />
      ) : null}
      <CatalogMenuBusinessInfo
        business={business}
        standard={standard}
        premium={premium}
        branding={branding}
        tc={tc}
      />
    </header>
  );
}
